import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Upload, File, Trash2, Download, X, Save } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const Documents = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamanho máximo (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
  };

  const handleSaveDocument = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      
      reader.onload = async () => {
        const base64Data = reader.result;
        
        await axios.post(
          `${API}/documents`,
          {
            filename: selectedFile.name,
            file_data: base64Data,
            file_size: selectedFile.size,
            content_type: selectedFile.type
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        setSelectedFile(null);
        await fetchDocuments();
        setUploading(false);
      };
      
      reader.onerror = () => {
        console.error('Error reading file');
        alert('Erro ao ler o arquivo');
        setUploading(false);
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Erro ao enviar documento');
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;

    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Erro ao excluir documento');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Meus Documentos</h1>
          <p className="text-gray-600">Gerencie seus arquivos</p>
        </div>
      </div>

      {/* Upload Area */}
      <div className="bg-white rounded-lg shadow-md p-8 border-2 border-dashed border-gray-300 hover:border-arcano-blue transition-colors">
        {!selectedFile ? (
          <div className="text-center">
            <Upload className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Enviar Documento
            </h3>
            <p className="text-gray-600 mb-4">Clique para selecionar um arquivo</p>
            <label className="inline-block">
              <span className="bg-arcano-blue text-white px-6 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors cursor-pointer">
                Selecionar Arquivo
              </span>
              <input
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </label>
            <p className="text-sm text-gray-500 mt-2">
              Formatos aceitos: PDF, DOC, DOCX, JPG, PNG (máx. 10MB)
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* File Preview */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="bg-arcano-blue p-3 rounded-lg">
                  <File size={24} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
              <button
                onClick={handleCancelFile}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Remover"
              >
                <X size={20} />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={handleCancelFile}
                disabled={uploading}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveDocument}
                disabled={uploading}
                className="flex items-center gap-2 bg-arcano-blue text-white px-6 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Salvar Documento
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-md border-2 border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            Documentos Enviados ({documents.length})
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-arcano-blue mx-auto"></div>
              <p className="text-gray-600 mt-2">Carregando...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              <File size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Nenhum documento enviado ainda</p>
            </div>
          ) : (
            documents.map((doc) => (
              <div key={doc.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-arcano-blue/10 p-3 rounded-lg">
                      <File size={24} className="text-arcano-blue" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{doc.filename}</p>
                      <p className="text-sm text-gray-600">
                        {formatFileSize(doc.file_size)} • {formatDate(doc.uploaded_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {doc.file_url && (
                      <button
                        onClick={() => window.open(doc.file_url, '_blank')}
                        className="p-2 text-arcano-blue hover:bg-arcano-blue/10 rounded-md transition-colors"
                        title="Download"
                      >
                        <Download size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Excluir"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
